import React, { useMemo } from 'react';
import { Certification, RoleplayCardProps, TextContent } from '@/components';
import { ICertification, ISimulation } from '@/types';
import type { GlobalState } from '@/store/types';

export interface CertificationsListProps {
  /**
   * Array of certifications to display
   */
  certifications: ICertification[];

  /**
   * Error state indicator for when certifications fail to load
   */
  error?: GlobalState['certificationsError'];

  /**
   * Loading state indicator
   */
  isLoading?: boolean;

  /**
   * Callback for sharing a certification with the manager
   */
  onShareWithManager?: RoleplayCardProps['onShareWithManager'];

  /**
   * Callback for viewing the scorecard of a certification
   */
  onViewScoreCard: RoleplayCardProps['onViewScoreCard'];

  /**
   * Optional user ID for context in actions like
   * sharing with manager or viewing scorecards.
   */
  userId?: string;
}

/**
 * CertificationsList Component
 *
 * Renders a list of certifications or loading placeholders.
 */
export const CertificationsList: React.FC<CertificationsListProps> = ({
  certifications = [],
  error = null,
  isLoading = false,
  onShareWithManager,
  onViewScoreCard,
  userId,
}) => {
  /**
   * Memoized certifications list to optimize rendering.
   * If loading, returns placeholder certifications.
   */
  const memoizedCertifications = useMemo(() => {
    if (isLoading) {
      // Return an array of placeholder certifications for loading state
      return Array.from({ length: 3 }, (_, index) => {
        const loadingSimulations: ISimulation[] = Array.from({ length: 1 }, (_, simIndex) => ({
          dateCompleted: '',
          duration: '',
          emCode: '',
          id: simIndex,
          name: `Loading Simulation ${simIndex + 1}`,
          personaId: 0,
          rubricId: '',
          score: 0,
          status: 'in-progress',
          title: `Loading Simulation ${simIndex + 1}`,
          type: 'role-play',
          uuid: `loading-${index}-sim-${simIndex}`,
          history: ['1', '2', '3'],
          description: `Loading description for Simulation ${simIndex + 1}`,
        }));

        const loadingCert: ICertification = {
          dueDate: '',
          id: index,
          progress: 0,
          simulations: loadingSimulations,
          status: 'in-progress',
          title: `Loading Certification ${index + 1}`,
          uuid: `loading-${index}`,
        };

        return loadingCert;
      });
    }

    if (error) {
      // Return an empty array or a specific error state if needed
      return [];
    }

    return certifications;
  }, [certifications, error, isLoading]);

  if (error) {
    return (
      <div className="space-y-5">
        <div className="border-red-accent-500 rounded-lg bg-neutral-white ring-2 ring-inset ring-accent-red-500">
          <div className="w-full p-6 text-start text-semantic-text-primary">
            <TextContent
              className="mb-2"
              color="error"
              dataContent="Certification Error Title"
              label="Error"
              testId="certification-error-title"
              type="paragraph"
              variant="bodyMedium"
              weight="bold"
            />
            <TextContent
              dataContent="Certification Error Description"
              label={`${error.message} (Status: ${error.status})`}
              testId="certification-error-description"
              type="paragraph"
              variant="bodyMedium"
            />
          </div>
        </div>
      </div>
    );
  }

  if (!isLoading && !error && memoizedCertifications.length === 0) {
    return (
      <div className="space-y-5" data-testid="certifications-empty-state">
        <div className="rounded-lg bg-neutral-white">
          <div className="w-full p-6 text-start text-semantic-text-primary">
            <TextContent
              className="mb-2"
              color="neutral-black"
              label="Sorry, no certifications found"
              testId="certification-empty-title"
              type="paragraph"
              variant="bodyMedium"
              weight="bold"
            />
            <TextContent
              label="Looks like you don't have any certifications assigned yet. Please check back later or contact your administrator for more information."
              testId="certification-empty-description"
              type="paragraph"
              variant="bodyMedium"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {memoizedCertifications.map((cert, i) => (
        <Certification
          certification={cert}
          isCollapsed={i !== 0}
          isCollapsible={true}
          isLoading={isLoading}
          key={cert.uuid}
          onShareWithManager={onShareWithManager}
          onViewScoreCard={onViewScoreCard}
          userId={userId}
        />
      ))}
    </div>
  );
};
